/**
 * The tasting dashboard's client. Besides the dashboard's own two routes it
 * wraps the three it acts through, which belong to other parts of the API:
 * the admin registration PATCH (quick paid / waived), the slot `assign` from the
 * two-step reveal, and the outreach `nudges`.
 */
import type {
  NudgeReason,
  NudgeResult,
  RegistrationAdminView,
  RegistrationPaymentStatus,
  TastingDashboard,
} from '@gwc/shared';
import { api } from '../lib/api';
import type { WineRow } from './models';

export interface WineReviewResult {
  wineReviewedAt: string | null;
  wineReviewedByName: string | null;
}

export interface NudgeInput {
  reason: NudgeReason;
  /** The ticked rows. Omit to reach everyone currently in that state. */
  personIds?: string[];
  note?: string;
}

export const dashboardApi = {
  get: (eventId: string) =>
    api.get<{ data: TastingDashboard }>(`/events/${eventId}/dashboard`).then((r) => r.data),

  setWineReviewed: (eventId: string, registrationId: string, reviewed: boolean) =>
    api
      .post<{ data: WineReviewResult }>(
        `/events/${eventId}/registrations/${registrationId}/wine-review`,
        { reviewed },
      )
      .then((r) => r.data),

  /** Quick payment fix (W1's admin PATCH): mark paid, waived or refunded. */
  setPaymentStatus: (
    eventId: string,
    registrationId: string,
    paymentStatus: RegistrationPaymentStatus,
  ) =>
    api
      .patch<{ data: RegistrationAdminView }>(
        `/events/${eventId}/registrations/${registrationId}`,
        { paymentStatus },
      )
      .then((r) => r.data),

  /** Bag a registration's wine under a letter, or clear the slot with null. */
  assignSlot: (eventId: string, wineId: string, registrationId: string | null) =>
    api
      .post<{ data: { wine: WineRow } }>(`/events/${eventId}/wines/${wineId}/assign`, {
        registrationId,
      })
      .then((r) => r.data.wine),

  nudge: (eventId: string, body: NudgeInput) =>
    api.post<{ data: NudgeResult }>(`/events/${eventId}/nudges`, body).then((r) => r.data),
};
